import { useState, useEffect } from 'react';

import uuid from 'core/utilities/uuid';

export const DEFAULT_PREVIEW_AMT = 100;

const roundTo = (value, decimals) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const isActiveAmount = amount => !amount._destroy && !amount.destroy;

/* A multiplier only has a dollar equivalent relative to a base donation, so DEFAULT_PREVIEW_AMT
   is the basis for both directions. Multipliers keep extra precision so that sub-dollar amounts
   survive a round trip. */
const convertAmountForType = (amount, toMultiplier) => {
  const numericAmount = parseFloat(amount);
  if (Number.isNaN(numericAmount)) return '';

  return toMultiplier
    ? roundTo(numericAmount / DEFAULT_PREVIEW_AMT, 6)
    : roundTo(numericAmount * DEFAULT_PREVIEW_AMT, 2);
};

/* Static and dynamic amounts both live in page_amounts, tagged by _isMultiplier, and the toggle
   only changes which of the two is rendered. Rebuild the list being switched to from the one
   being left behind so the donor sees the same number of buttons, labels and preselection. */
export const mirrorAmountsForAmountType = (pageAmounts, toMultiplier) => {
  const activeAmounts = (pageAmounts || []).filter(isActiveAmount);
  const source = activeAmounts.filter(amt => !!amt._isMultiplier !== toMultiplier);
  const target = activeAmounts.filter(amt => !!amt._isMultiplier === toMultiplier);

  // Nothing to mirror from, so leave whatever the new type already has alone.
  if (!source.length) return pageAmounts;

  // Reuse the target rows positionally so saved amounts keep their id instead of being recreated.
  const mirrored = source.map((sourceAmount, index) => ({
    ...(target[index] || { id: uuid(), _isNew: true }),
    _isMultiplier: toMultiplier,
    amount: convertAmountForType(sourceAmount.amount, toMultiplier),
    description: sourceAmount.description || '',
    preselected: !!sourceAmount.preselected,
  }));

  /* Target rows past the end of the source list are dropped outright when they were never saved
     and flagged for removal when the server still knows about them. */
  const removedTargets = target
    .slice(source.length)
    .filter(amt => !amt._isNew)
    .map(amt => ({ ...amt, _destroy: true }));

  const inactiveAmounts = (pageAmounts || []).filter(amt => !isActiveAmount(amt));

  return [...mirrored, ...source, ...removedTargets, ...inactiveAmounts];
};

const uniqueByAmount = items => {
  const seen = new Set();
  return items.filter(({ amount }) => {
    if (seen.has(amount)) return false;
    seen.add(amount);
    return true;
  });
};

export const useMultiplierAmounts = ({ previewAmount, editedUpsell, maxPersonalDonation }) => {
  const maxDonationAmount = parseFloat(editedUpsell.maximum_amount) || maxPersonalDonation;
  const minDonationAmount = parseFloat(editedUpsell.minimum_amount) || 1;

  const [amounts, setAmounts] = useState([]);
  const [preselectedIndex, setPreselectedIndex] = useState(0);
  const { page_amounts, multiplier_amounts, round_values } = editedUpsell;

  useEffect(() => {
    const pageAmounts = (page_amounts || []).filter(amt => !amt._destroy && amt._isMultiplier === !!multiplier_amounts);
    const preselected = pageAmounts.findIndex(pa => pa.preselected);
    setPreselectedIndex(preselected);
  }, [page_amounts, multiplier_amounts]);

  useEffect(() => {
    if (!multiplier_amounts) {
      const newAmounts = (page_amounts || [])
        .filter(amt => !amt._isMultiplier && !amt._destroy)
        .map(amt => ({
          amount: amt.amount,
          description: amt.description || '',
        }));

      setAmounts(uniqueByAmount(newAmounts));
      return;
    }

    // get only multiplier amounts, set to fixed if needed, set to min/max if given, filter out dups
    const newAmounts = page_amounts
      .filter(amt => amt._isMultiplier && !amt._destroy)
      .map(amt => {
        let currentAmount = previewAmount === '' ? DEFAULT_PREVIEW_AMT : previewAmount;
        if (currentAmount > maxDonationAmount) {
          currentAmount = maxDonationAmount;
        } else if (currentAmount < minDonationAmount) {
          currentAmount = minDonationAmount;
        }

        let text = currentAmount * amt.amount;

        if (round_values) {
          // round to nearest 1 if under 10 else round to nearest 10 if above 10
          text = text >= 10 ? Math.round(text / 10) * 10 : Math.round(text);
          /* When comparing these numbers we only care about the first two digits of
          precision so if that is the same as the rounded value, use the rounded
          value. */
        } else if (parseFloat(text.toFixed(2)) === Math.round(text)) {
          text = Math.round(text);
        } else if (Math.round(text) !== text) {
          // if is float then make sure we have two decimal places
          text = text.toFixed(2);
        }

        return {
          amount: text,
          description: amt.description || '',
        };
      });

    setAmounts(uniqueByAmount(newAmounts));
  }, [minDonationAmount, maxDonationAmount, previewAmount, page_amounts, round_values, multiplier_amounts]);

  return [amounts, preselectedIndex];
};
